import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { Navigation, ExternalLink } from "react-feather";

import CONSTANTS from "../constants";
import { Button, palette, mediaMin, fonts } from "../ui-components";

const DetailsPopup = ({ place }) => {
  const { place_id, distance, name, photos, price_level } = place;
  const [placeDetails, setPlaceDetails] = useState();

  useEffect(() => {
    if (window.google) {
      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      service.getDetails({ placeId: place_id }, (result) => {
        setPlaceDetails(result);
      });
    }
  }, [place_id]);

  /**
   * Open a new google maps tab with the destination address
   */
  const handleDirectionsClick = () => {
    if (placeDetails.formatted_address) {
      const address = placeDetails.formatted_address
        .replace(/( )/g, "+")
        .replace(/(,)/g, "");
      window.open(`https://www.google.com/maps?daddr=${address}`);
    }
  };

  const getPopupPhotoUrl = () => {
    if (!photos || !photos.length > 0) return "";
    const url = `https://maps.googleapis.com/maps/api/place/photo?photoreference=${photos[0].photo_reference}&sensor=false&maxheight=500&maxwidth=500&key=${CONSTANTS.APIKEY}`;
    return url;
  };

  /**
   * Calculate closing time based on current date
   */
  const getCloseTime = () => {
    const date = new Date();
    const day = date.getDay();
    if (
      !placeDetails.opening_hours.periods ||
      placeDetails.opening_hours.periods.length < 7 ||
      !placeDetails.opening_hours.periods[day].close ||
      !placeDetails.opening_hours.periods[day].close.time
    )
      return ` ${String.fromCharCode(183)} Unknown Closing Time`;
    const closedTime =
      placeDetails.opening_hours.periods[day].close.time.toString();
    let time = closedTime.slice(0, 2) + ":" + closedTime.slice(2, 4);
    time = time
      .toString()
      .match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];
    if (time.length > 1) {
      time = time.slice(1);
      time[5] = +time[0] < 12 ? "AM" : "PM";
      time[0] = +time[0] % 12 || 12;
    }
    return " " + String.fromCharCode(183) + " Closes " + time.join("");
  };

  /**
   * Render Hours Open
   * NOTE: Google Places API will no longer support this feature in a future release
   * There will be an alternative method to evalulate if the business is open
   */
  const openingHours = () => {
    if (placeDetails.opening_hours.isOpen()) {
      return (
        <HoursText>
          <strong>Hours today:</strong> <span className={"open"}>Open</span>
          {getCloseTime()}
        </HoursText>
      );
    } else {
      return (
        <HoursText>
          <strong>Hours today:</strong> <span className={"closed"}>Closed</span>
        </HoursText>
      );
    }
  };

  const getPriceLevel = () => {
    switch (price_level) {
      case 1:
        return "$";
      case 2:
        return "$$";
      case 3:
        return "$$$";
      case 4:
        return "$$$$";
      case 5:
        return "$$$$$";
      default:
        return "$";
    }
  };

  function get_domain_from_url(url) {
    const a = document.createElement("a");
    a.setAttribute("href", url);
    return a.hostname;
  }

  return (
    <Wrapper>
      {placeDetails && (
        <>
          <ImageContainer bgImage={getPopupPhotoUrl()}>
            {photos && photos.length > 0 && (
              <ul>
                {photos[0].html_attributions.map((attribution, index) => (
                  <li
                    key={index}
                    dangerouslySetInnerHTML={{
                      __html: attribution.replace("<a", '<a target="blank"'),
                    }}
                  ></li>
                ))}
              </ul>
            )}
          </ImageContainer>
          <ContentContainer>
            <List>
              {distance && <Item>{distance.toFixed(1)} miles</Item>}
              <Item>{getPriceLevel()}</Item>
            </List>
            <TitleHeading size={5}>{name}</TitleHeading>
            <SubHeading>{placeDetails.formatted_phone_number}</SubHeading>
            <SubHeading style={{ marginBottom: 20 }}>
              {placeDetails.formatted_address}
            </SubHeading>
            {(placeDetails.website || place.alternativeWebsite) && (
              <Website>
                <a
                  href={place.alternativeWebsite || placeDetails.website}
                  target="_blank"
                  rel="noreferrer"
                >
                  {get_domain_from_url(placeDetails.website)}
                  <ExternalLink size={12} style={{ marginLeft: 5 }} />
                </a>
              </Website>
            )}
            <Reviews>
              {placeDetails.rating && (
                <>
                  <span>{placeDetails.rating}</span>{" "}
                  <span className="star">★</span>{" "}
                </>
              )}
              <a href={placeDetails.url} target="_blank" rel="noreferrer">
                {placeDetails.user_ratings_total
                  ? placeDetails.user_ratings_total
                  : 0}{" "}
                Reviews <ExternalLink size={12} style={{ marginLeft: 5 }} />
              </a>
            </Reviews>
            {placeDetails.opening_hours && openingHours()}
            <Button onClick={handleDirectionsClick}>
              <Navigation size={14} style={{ marginRight: 5 }} />
              Driving Directions
            </Button>
          </ContentContainer>
        </>
      )}
    </Wrapper>
  );
};

export { DetailsPopup };

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 400px;

  ${mediaMin.tablet_md`
    flex-direction: row;
  `}
`;

const ContentContainer = styled.div`
  flex: 50%;
  padding: 16px 24px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.25);
  border-style: solid;
  background-color: ${palette.white};
  border-radius: 0 0 40px 40px;

  ${mediaMin.tablet_md`
    padding: 50px;
    border-radius: 0 60px 60px 0;
  `}
`;

const ImageContainer = styled.div`
  background-image: url(${({ bgImage }) => bgImage || ""});
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-color: ${palette.gray};
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.25);
  width: 100%;
  flex: 50%;
  border-radius: 40px 40px 0 0;
  min-height: 300px;
  position: relative;

  ${mediaMin.tablet_md`
    padding: 50px;
    border-radius: 60px 0 0 60px;
    min-height: auto;
  `}

  ul {
    position: absolute;
    bottom: 0;
    right: 0;
    font-size: 11px;
    padding: 5px;

    li {
      a {
        color: ${palette.white};
      }
    }
  }
`;

const List = styled.ul`
  display: flex;
  align-items: center;

  li:first-child {
    padding-left: 0;
  }

  li:last-child {
    padding-right: 0;
    border-right: none;
  }
`;

const Item = styled.li`
  font-size: 11px;
  padding: 0 8px;
  color: ${({ color }) => (color ? color : palette.text)};
  border-right: 1px solid #ccc;

  span.star {
    color: ${palette.yellow};
  }
`;

const TitleHeading = styled.h2`
  font-size: 18px;
  margin: 10px 0;
  line-height: 1.5;
  font-family: ${fonts.primary};
  color: ${palette.primary};

  ${mediaMin.tablet_md`
    font-size: 22px
  `}
`;

const SubHeading = styled.h4`
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 0;
`;

const HoursText = styled.p`
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 15px;

  span.open {
    color: ${palette.green};
  }

  span.closed {
    color: ${palette.secondary};
  }
`;

const Website = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;

  a {
    color: ${palette.text};

    &:hover {
      color: ${palette.primary};
    }
  }
`;

const Reviews = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  margin: 20px 0;
  line-height: 1;

  a {
    color: ${palette.text};

    &:hover {
      color: ${palette.primary};
    }
  }

  span {
    margin-right: 5px;
  }

  span.star {
    color: ${palette.yellow};
  }
`;
